import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

import Highcharts from 'highcharts/highstock';
import { ChartsConfigBuilder } from './charts-config-builder';

export interface LineConfiguration {
  color?: string;
  dashStyle?: string;
  lineWidth?: number;
  width?: number;
}

export const LINE_CONFIGURATION = new InjectionToken<Array<LineConfiguration>>('lineConfiguration');

@Injectable()
export class ChartsConfigService {
  private readonly DEFAULT_LINE_CONFIGURATION: ReadonlyArray<LineConfiguration> = [
    {
      color: '#008a00',
      dashStyle: 'Solid',
      lineWidth: 2,
      width: 2
    },
    {
      color: '#00485e',
      dashStyle: 'LongDash',
      lineWidth: 2,
      width: 2
    },
    {
      color: '#a65207',
      dashStyle: 'ShortDot',
      lineWidth: 2,
      width: 2
    },
    {
      color: '#4b94b9',
      dashStyle: 'ShortDashDotDot',
      lineWidth: 2,
      width: 2
    },
    {
      color: '#005B18',
      dashStyle: 'LongDashDot',
      lineWidth: 2,
      width: 2
    }
  ];

  constructor(@Optional() @Inject(LINE_CONFIGURATION) private readonly lineConfiguration?: ReadonlyArray<LineConfiguration>) {
    if (this.lineConfiguration === undefined || this.lineConfiguration === null) {
      this.lineConfiguration = this.DEFAULT_LINE_CONFIGURATION;
    }
  }

  /**
   *
   * @param idx Line number on chart legend. Starts with zero.
   * @param config Line configuration object where default properties will be copied
   * @returns Default chart line configuration as per DCX standards
   */
  getLineConfiguration(idx: number, config: Highcharts.SeriesOptions): Highcharts.SeriesOptionsType {
    return {
      ...this.lineConfiguration[idx],
      ...config
    } as Highcharts.SeriesOptionsType;
  }

  /**
   * Return new ChartsConfigBuilder object
   *
   * @param options Chart options
   * @returns
   */
  createUpdateConfigBuilder(options: Highcharts.Options): ChartsConfigBuilder {
    return new ChartsConfigBuilder(options);
  }

  /**
   * @param seriesName The name of the initial series.
   * @param seriesData The initial series data to be applied to the configuration.
   * @returns description: A builder for the chartConfig. The builder contains a number of
   * helpful methods to augment the returned config. You can access the config data by invoking
   * getConfig() on the builder object.
   */
  createChartConfig(seriesName: string, seriesData: Array<unknown>): ChartsConfigBuilder {
    const configBuilder = new ChartsConfigBuilder(this.makeChartConfig());
    if (seriesName !== undefined && seriesData !== undefined) {
      configBuilder.addSeries(seriesName, seriesData);
    }

    return configBuilder;
  }

  /**
   * @returns description: A builder for the chartConfig. The builder contains a number of
   * helpful methods to augment the returned config. You can access the config data by invoking
   * getConfig() on the builder object.
   */
  createStockChartConfig(): ChartsConfigBuilder {
    return new ChartsConfigBuilder(this.makeStockChartConfig());
  }

  /**
   * Map array of objects to Highcharts format
   *
   * @param arrayOfObjects
   * @param xPropertyName
   * @param yPropertyName
   * @returns
   */
  mapArrayOfObjectsToHighchartsFormat(arrayOfObjects: Array<Record<string, unknown>>,
                                      xPropertyName: string,
                                      yPropertyName: string): Array<{ x; y }> {
    return arrayOfObjects
      .map((element: Record<string, unknown>): { x: unknown; y: unknown } => ({ x: element[xPropertyName], y: element[yPropertyName] }));
  }

  /**
   * Creates a default stock chart options specification.
   *
   * @returns
   */
  private makeStockChartConfig(): Highcharts.Options {
    return {
      accessibility: {
        enabled: true,
        description: 'Stock chart',
        screenReaderSection: {
          beforeChartFormat: ''
        }
      },
      xAxis: {
        type: 'datetime'
      },
      credits: {
        enabled: false
      }
    };
  }

  /**
   * Creates a default chart options specification.
   *
   * @returns
   */
  private makeChartConfig(): Highcharts.Options {
    return {
      credits: {
        enabled: false
      }
    };
  }
}
